<script setup lang="ts">
import { Head } from "@inertiajs/vue3"
import { SbList } from "@unio/saltbox-ui"
import { ref } from "vue"

import ContainerFormSlideover from "@/components/containers/ContainerFormSlideover.vue"
import DeleteConfirmModal from "@/components/DeleteConfirmModal.vue"
import DetailRow from "@/components/DetailRow.vue"
import DuplicateItemModal from "@/components/items/DuplicateItemModal.vue"
import ImageManageSlideover from "@/components/items/ImageManageSlideover.vue"
import ItemArchiveModal from "@/components/items/ItemArchiveModal.vue"
import ItemContainerChildren from "@/components/items/ItemContainerChildren.vue"
import ItemDetailGrid from "@/components/items/ItemDetailGrid.vue"
import ItemDispositionDetails from "@/components/items/ItemDispositionDetails.vue"
import ItemHeader from "@/components/items/ItemHeader.vue"
import ItemImageGallery from "@/components/items/ItemImageGallery.vue"
import ItemLinkedItems from "@/components/items/ItemLinkedItems.vue"
import ItemLinkSlideover from "@/components/items/ItemLinkSlideover.vue"
import ItemLoanBanner from "@/components/items/ItemLoanBanner.vue"
import ItemStatusBar from "@/components/items/ItemStatusBar.vue"
import ItemTagBar from "@/components/items/ItemTagBar.vue"
import LoanFormSlideover from "@/components/items/LoanFormSlideover.vue"
import LabeledSection from "@/components/LabeledSection.vue"
import LoanListItem from "@/components/LoanListItem.vue"
import LocationBreadcrumb from "@/components/LocationBreadcrumb.vue"
import TextBlockRow from "@/components/TextBlockRow.vue"
import { useContainerEdit } from "@/composables/useContainerEdit"
import { useDeleteItem } from "@/composables/useDeleteItem"
import { useDuplicateItem } from "@/composables/useDuplicateItem"
import { useItemArchive } from "@/composables/useItemArchive"
import { useItemLinks } from "@/composables/useItemLinks"
import { useNavbar } from "@/composables/useNavbar"
import { ICON } from "@/lib/icons"
import { itemsPath } from "@/routes"
import type {
  ItemDetail,
  LoanSummary,
  PersonOption,
  SelectOption,
  SiblingContainer,
} from "@/types/models"

const props = defineProps<{
  item: ItemDetail
  loan_history: LoanSummary[]
  people: PersonOption[]
  parent_containers?: SelectOption[]
  item_types?: SelectOption[]
  sibling_containers?: SiblingContainer[]
}>()

const { title } = useNavbar({ title: props.item.name })

const imageManageOpen = ref(false)
const loanFormOpen = ref(false)
const { linkOpen, unlinkItem, onLinked } = useItemLinks(props.item.id)
const { duplicateOpen, duplicating, confirmDuplicate } = useDuplicateItem(props.item.id)
const {
  archiveOpen,
  restoreOpen,
  archiving,
  formErrors: archiveErrors,
  openArchive,
  confirmArchive,
  confirmRestore,
} = useItemArchive(props.item.id)
const { containerFormOpen, containerEditData, hasContainerProperties } = useContainerEdit(
  props.item,
)
const {
  deleteModalOpen,
  deleting,
  reassignTo,
  reparentMode,
  childCount,
  deleteDisabled,
  openDelete,
  confirmDelete,
} = useDeleteItem(props.item)
</script>

<template>
  <Head :title="title" />

  <LocationBreadcrumb :ancestors="item.ancestors" />

  <div class="flex flex-col gap-8 md:flex-row">
    <!-- Photo column -->
    <div class="flex flex-col gap-4 md:sticky md:top-8 md:w-2/5 md:shrink-0 md:self-start">
      <ItemImageGallery :images="item.images" :alt="item.name" />
      <UButton
        :icon="ICON.edit"
        label="Edit photos"
        color="neutral"
        variant="subtle"
        size="sm"
        block
        @click="
          () => {
            imageManageOpen = true
          }
        "
      />
    </div>

    <!-- Details column -->
    <div class="flex min-w-0 flex-1 flex-col gap-6">
      <ItemHeader
        :name="item.name"
        :type-name="item.item_type?.name"
        :item-id="item.id"
        :is-container="item.is_container"
        :has-active-loan="!!item.active_loan"
        :archived="!!item.archived_at"
        :system="item.system"
        @record-loan="loanFormOpen = true"
        @edit-container="containerFormOpen = true"
        @duplicate-item="duplicateOpen = true"
        @delete-item="openDelete"
        @archive-item="openArchive"
        @restore-item="restoreOpen = true"
      />

      <ItemStatusBar
        :quantity="item.quantity"
        :consumable="item.consumable"
        :inventoried-at="item.inventoried_at"
        :archived-at="item.archived_at"
      />

      <ItemDispositionDetails v-if="item.disposition" :disposition="item.disposition" />

      <ItemTagBar :tags="item.tags" />

      <SbList v-if="item.description || item.notes || item.condition" bordered>
        <TextBlockRow v-if="item.description" label="Description" :value="item.description" />
        <TextBlockRow v-if="item.notes" label="Notes" :value="item.notes" />
        <TextBlockRow v-if="item.condition" label="Condition" :value="item.condition" />
      </SbList>

      <ItemDetailGrid
        :parent-name="item.parent?.name"
        :person-name="item.person?.name"
        :asset-code="item.asset_code"
        :barcode="item.barcode"
        :model-number="item.model_number"
        :serial-number="item.serial_number"
      />

      <SbList v-if="hasContainerProperties" bordered>
        <DetailRow
          v-if="item.container_properties!.assigned_location"
          label="Location"
          :value="item.container_properties!.assigned_location"
        />
        <DetailRow
          v-if="item.container_properties!.access_method"
          label="Access"
          :value="item.container_properties!.access_method"
        />
        <DetailRow
          v-if="item.container_properties!.access_notes"
          label="Access Notes"
          :value="item.container_properties!.access_notes"
        />
      </SbList>

      <ItemLoanBanner
        v-if="item.active_loan"
        :loan-id="item.active_loan.id"
        :item-id="item.id"
        :direction="item.active_loan.direction"
        :person-name="item.active_loan.person_name"
        :loaned-on="item.active_loan.loaned_on"
        :expected-return-on="item.active_loan.expected_return_on"
        :overdue="item.active_loan.overdue"
      />

      <LabeledSection v-if="loan_history.length" title="Loan History" :count="loan_history.length">
        <LoanListItem v-for="loan in loan_history" :key="loan.id" :loan="loan" show-person static />
      </LabeledSection>

      <div class="flex flex-col gap-2">
        <ItemLinkedItems
          v-if="item.linked_items.length"
          :items="item.linked_items"
          editable
          @unlink="unlinkItem"
        />
        <UButton
          :icon="ICON.link"
          label="Link items"
          color="neutral"
          variant="subtle"
          size="sm"
          block
          @click="
            () => {
              linkOpen = true
            }
          "
        />
      </div>

      <ItemContainerChildren
        v-if="item.is_container && (item.child_items?.length ?? 0) > 0"
        :items="item.child_items!.slice(0, 10)"
        :total-count="item.child_items!.length"
        :see-all-to="itemsPath({ container_id: item.id })"
      />
    </div>
  </div>

  <LoanFormSlideover v-model:open="loanFormOpen" :item-id="item.id" :people="people" />

  <ContainerFormSlideover
    v-if="item.is_container"
    v-model:open="containerFormOpen"
    :container="containerEditData"
    :parent-containers="parent_containers ?? []"
    :item-types="item_types ?? []"
  />

  <ImageManageSlideover
    v-model:open="imageManageOpen"
    :item-id="item.id"
    :images="item.images"
    :is-container="item.is_container"
  />

  <ItemLinkSlideover v-model:open="linkOpen" :item-id="item.id" @linked="onLinked" />

  <DuplicateItemModal
    v-model:open="duplicateOpen"
    :item-name="item.name"
    :quantity="item.quantity"
    :loading="duplicating"
    @confirm="confirmDuplicate"
  />

  <ItemArchiveModal
    v-model:open="archiveOpen"
    :item-name="item.name"
    :loading="archiving"
    :errors="archiveErrors"
    @confirm="confirmArchive"
  />

  <DeleteConfirmModal
    v-model:open="restoreOpen"
    :title="`Restore ${item.name}?`"
    action-label="Restore"
    action-color="primary"
    :action-icon="ICON.restore"
    :loading="archiving"
    @confirm="confirmRestore"
  >
    <UAlert
      v-if="Object.keys(archiveErrors).length"
      color="error"
      :description="Object.values(archiveErrors).join(' ')"
      class="mb-4"
    />
    <p class="text-sm">This returns the item to active inventory and clears its disposition.</p>
  </DeleteConfirmModal>

  <DeleteConfirmModal
    v-model:open="deleteModalOpen"
    :title="`Delete ${item.name}?`"
    :loading="deleting"
    :disabled="deleteDisabled"
    @confirm="confirmDelete"
  >
    <template v-if="childCount === 0">
      <p class="text-sm">This action cannot be undone.</p>
    </template>
    <template v-else>
      <p class="text-sm">
        This container has {{ childCount }} {{ childCount === 1 ? "item" : "items" }}. Where should
        they go?
      </p>
      <URadioGroup
        v-model="reparentMode"
        :items="[
          { label: 'Move up one level', value: 'up' },
          ...(sibling_containers?.length
            ? [{ label: 'Move to another container', value: 'move' }]
            : []),
        ]"
        class="mt-4"
      />
      <USelectMenu
        v-if="reparentMode === 'move' && sibling_containers?.length"
        v-model="reassignTo"
        :items="sibling_containers!.map((c) => ({ label: c.name, value: c.id }))"
        value-key="value"
        placeholder="Select container..."
        class="mt-2 ml-6 w-full"
      />
    </template>
  </DeleteConfirmModal>
</template>
